import {
  businessProfileInputSchema,
  contactPhoneSchema,
  type BusinessProfileInput,
  type SocialLink,
} from '@mybusinessmate/schema';

/** Everything collected by the five onboarding questions, kept as the user typed it. */
export interface OnboardingDraft {
  industry?: BusinessProfileInput['industry'];
  /** Free text, only meaningful when industry === 'other'. */
  customIndustry: string;
  businessName: string;
  googlePlaceId?: string;
  /** Display-only: the address of the picked Google place, or one typed manually. Not sent (the server derives it). */
  address: string;
  hasWebsite: boolean;
  websiteUrl: string;
  instagramHandle: string;
  facebookUrl: string;
  otherUrl: string;
  contactPhone: string;
  whatsappConsent: boolean;
}

export const emptyDraft: OnboardingDraft = {
  customIndustry: '',
  businessName: '',
  address: '',
  hasWebsite: true,
  websiteUrl: '',
  instagramHandle: '',
  facebookUrl: '',
  otherUrl: '',
  contactPhone: '',
  whatsappConsent: true,
};

/** Adds https:// to bare domains ("paradisebiryani.com"), leaves full URLs alone, and maps blank to ''. */
export function normalizeUrl(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/** Blank is fine (the step is optional); otherwise it must parse as a URL with a real host, e.g. yourbusiness.com. */
export function validateUrl(raw: string): string | undefined {
  const value = normalizeUrl(raw);
  if (!value) return undefined;
  try {
    const { hostname } = new URL(value);
    if (hostname.includes('.') && !hostname.endsWith('.')) return undefined;
  } catch {
    // fall through to the message
  }
  return 'Enter a valid website address, like yourbusiness.com';
}

/** "+91  98765 43210", "098765 43210", "9876543210" -> "9876543210". Leaves anything else digit-only for validation to reject. */
export function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

export function validatePhone(raw: string): string | undefined {
  const result = contactPhoneSchema.safeParse(normalizePhone(raw));
  return result.success ? undefined : result.error.issues[0]?.message;
}

/** "@paradisebiryani", "paradisebiryani" or a pasted instagram.com URL -> a canonical profile URL. */
export function instagramUrl(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || /^(www\.)?instagram\.com\//i.test(value)) return normalizeUrl(value);
  return `https://instagram.com/${value.replace(/^@/, '')}`;
}

/** "facebook.com/paradisebiryani" or a page name -> a URL. */
export function facebookUrl(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || /^(www\.)?facebook\.com\//i.test(value)) return normalizeUrl(value);
  return `https://facebook.com/${value}`;
}

export function socialLinksFromDraft(draft: OnboardingDraft): SocialLink[] {
  const links: SocialLink[] = [];
  const instagram = instagramUrl(draft.instagramHandle);
  const facebook = facebookUrl(draft.facebookUrl);
  const other = normalizeUrl(draft.otherUrl);
  if (instagram) links.push({ platform: 'instagram', url: instagram });
  if (facebook) links.push({ platform: 'facebook', url: facebook });
  if (other) links.push({ platform: 'other', url: other });
  return links;
}

/**
 * Builds the `POST /api/business/profile` body from the onboarding answers. Parsed through the shared
 * schema so a draft the API would reject (bad phone, "other" without text, malformed URL) fails here,
 * where the UI can send the user back to the right step.
 */
export function draftToProfileInput(draft: OnboardingDraft): BusinessProfileInput {
  return businessProfileInputSchema.parse({
    industry: draft.industry,
    ...(draft.industry === 'other' ? { customIndustry: draft.customIndustry.trim() } : {}),
    businessName: draft.businessName,
    ...(draft.googlePlaceId ? { googlePlaceId: draft.googlePlaceId } : {}),
    websiteUrl: draft.hasWebsite ? normalizeUrl(draft.websiteUrl) : '',
    socialLinks: socialLinksFromDraft(draft),
    contactPhone: normalizePhone(draft.contactPhone),
  });
}

export type OnboardingStep = 'industry' | 'business' | 'website' | 'socials' | 'phone';

/** Route of each assessment question, in order. */
export const ONBOARDING_PATH = {
  industry: '/subdomain/app/onboarding/industry',
  business: '/subdomain/app/onboarding/business',
  website: '/subdomain/app/onboarding/website',
  socials: '/subdomain/app/onboarding/socials',
  phone: '/subdomain/app/onboarding/phone',
} as const satisfies Record<OnboardingStep, string>;

/**
 * The first question whose answer is missing or invalid, or `null` when the assessment is finished. Mirrors each
 * step's own "can Continue" rule, so a half-done assessment resumes where it stopped instead of at question 1.
 */
export function firstIncompleteStep(draft: OnboardingDraft): OnboardingStep | null {
  if (!draft.industry || (draft.industry === 'other' && !draft.customIndustry.trim())) return 'industry';
  if (!draft.businessName.trim() || !(draft.googlePlaceId || draft.address.trim())) return 'business';
  if (draft.hasWebsite && (!draft.websiteUrl.trim() || validateUrl(draft.websiteUrl))) return 'website';
  if (validateUrl(draft.otherUrl)) return 'socials';
  if (validatePhone(draft.contactPhone)) return 'phone';
  return null;
}

/** Finished locally AND acceptable to the API: the only state in which creating an account and the audit make sense. */
export function isAssessmentComplete(draft: OnboardingDraft): boolean {
  if (firstIncompleteStep(draft) !== null) return false;
  try {
    draftToProfileInput(draft);
    return true;
  } catch {
    return false;
  }
}
